<template>
  <div class="dashboard">
    <!-- Hero Welcome Card -->
    <div class="dashboard-hero mb-4">
      <div class="dashboard-hero__content">
        <div class="dashboard-hero__left">
          <div class="dashboard-hero__avatar">
            <i class="bi bi-person-circle"></i>
          </div>
          <div>
            <div class="dashboard-hero__badge" :class="bisaLihatInsight ? 'badge-panitia' : 'badge-official'">
              <i class="bi" :class="bisaLihatInsight ? 'bi-shield-check' : 'bi-person-badge'"></i>
              {{ bisaLihatInsight ? 'PANITIA / ADMIN' : 'OFFICIAL KONTINGEN' }}
            </div>
            <h2 class="dashboard-hero__title">
              Selamat Datang, <span>{{ userName || 'Sahabat Olahraga' }}</span>! 👋
            </h2>
            <p class="dashboard-hero__subtitle" v-if="bisaLihatInsight">
              Kelola kejuaraan, data peserta, bagan pertandingan, dan verifikasi tagihan dalam satu panel terpadu.
            </p>
            <p class="dashboard-hero__subtitle" v-else>
              Pilih turnamen di bawah ini untuk mendaftarkan kontingen, menambah atlet, dan mengunduh petunjuk teknis kejuaraan.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section Header -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3 class="section-title">
        <i class="bi bi-trophy-fill text-accent me-2"></i> Turnamen
      </h3>
    </div>

    <div class="kartu-grid">
      <!-- Kartu Turnamen Modern -->
      <article
        v-for="tournament in tournaments"
        :key="tournament.id"
        class="kartu"
      >
        <div class="kartu__poster">
          <SmartImage
            :source="tournament.image"
            :alt="tournament.name"
            :label="tournament.name"
            ratio="3 / 4"
          />

          <span class="kartu__status" :class="`kartu__status--${infoStatus(tournament).tone}`">
            <span class="kartu__status-dot"></span>
            {{ labelStatus(tournament) }}
          </span>
        </div>

        <div class="kartu__isi">
          <h3 class="kartu__judul">{{ tournament.name }}</h3>

          <div class="kartu__aksi">
            <router-link
              v-if="bisaDaftar(tournament)"
              :to="{ name: 'contingent-registration', params: { id: tournament.id } }"
              class="button button-primary"
            >
              <i class="bi bi-pencil-square"></i> Daftar Sekarang
            </router-link>

            <router-link
              v-if="bisaLihatInsight"
              :to="{ name: 'tournament-insight', params: { id: tournament.id } }"
              class="button button-ghost"
            >
              <i class="bi bi-graph-up"></i> Detail Insight
            </router-link>

            <a
              v-if="tournament.document"
              :href="urlProposal(tournament)"
              target="_blank"
              rel="noopener noreferrer"
              class="button button-ghost"
              :class="{
                'is-downloading': mengunduhProposal[tournament.id] === 'loading',
                'is-success': mengunduhProposal[tournament.id] === 'success',
              }"
              download
              @click="saatKlikUnduhProposal(tournament, $event)"
            >
              <i v-if="mengunduhProposal[tournament.id] === 'loading'" class="bi bi-arrow-repeat ikon-putar text-danger me-1"></i>
              <i v-else-if="mengunduhProposal[tournament.id] === 'success'" class="bi bi-check-circle-fill text-success ikon-sukses me-1"></i>
              <i v-else class="bi bi-file-earmark-arrow-down"></i>
              {{ mengunduhProposal[tournament.id] === 'loading' ? 'Mengunduh...' : (mengunduhProposal[tournament.id] === 'success' ? 'Tersimpan!' : 'Unduh Proposal') }}
            </a>
          </div>
        </div>
      </article>

      <div v-if="!tournaments.length && !loading" class="kartu-kosong">
        <div class="empty-icon-circle mb-3">
          <i class="bi bi-trophy text-muted fs-1"></i>
        </div>
        <h5>Belum Ada Turnamen Aktif</h5>
        <p class="text-muted small">Turnamen yang dibuka akan muncul di halaman ini.</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import API from "@/config/api"; // Import the API base URL configuration
import { inject } from 'vue';
import { useToast } from 'vue-toastification';
import { infoStatus, STATUS } from '@/utils/status';
import { mediaUrl } from '@/utils/media';
import SmartImage from '@/components/ui/SmartImage.vue';

// Set axios base URL (if needed)
axios.defaults.baseURL = API.API_BASE_URL;

export default {
  name: "DashboardPage",

  components: { SmartImage },
  data() {
    return {
      userName: null, // Store user's name here
      tournaments: [], // Store tournament data
      searchQuery: "", // User's search input
      currentPage: 1, // Current page of results
      perPage: 10, // Results per page
      totalPages: 0, // Total number of pages
      prevPageUrl: null, // URL of the previous page
      nextPageUrl: null, // URL of the next page
      loading: false,
      mengunduhProposal: {},
    };
  },
  setup() {
    const permissions = inject('permissions', []); // Ensure permissions is always an array

    return { permissions, toast: useToast() };
  },
  computed: {
    /*
    | Kondisi lama ditulis  !permissions && !permissions.includes(...)
    | yang tidak pernah bisa benar: kalau permissions palsu, memanggil
    | .includes() padanya melempar galat. Akibatnya cabang "peserta" tidak
    | pernah terpakai dan semua orang melihat kalimat untuk panitia.
    */
    bisaLihatInsight() {
      const izin = this.permissions?.value ?? this.permissions;
      return Array.isArray(izin) && izin.includes('view insight');
    },
  },

  mounted() {
    this.loadUserData(); // Call method to load user data when component is mounted
    this.loadTournamentData(); // Load sample tournament data
  },
  methods: {
    /** Mengikuti sistem empat status; 'active' sudah tidak dipakai lagi. */
    bisaDaftar(turnamen) {
      return infoStatus(turnamen).bisaDaftar;
    },

    infoStatus,

    /** infoStatus mengembalikan status yang sudah dinormalkan di properti .status */
    labelStatus(turnamen) {
      return {
        [STATUS.DIBUKA]: 'Dibuka',
        [STATUS.SEGERA]: 'Segera hadir',
        [STATUS.SELESAI]: 'Selesai',
        [STATUS.SEMBUNYIKAN]: 'Disembunyikan',
      }[infoStatus(turnamen).status] || '-';
    },

    async loadUserData() {
      try {
        // Get the token from localStorage
        const token = localStorage.getItem("authToken");
        
        if (!token) {
          console.error("Token not found. User is not authenticated.");
          this.userName = "Guest";
          return;
        }

        // Fetch user data from the server
        const response = await axios.get("/user", {
          headers: {
            Authorization: `Bearer ${token}`, // Send token with the request
          },
        });

        if (response.data) {
          this.userName = response.data.user.name || "Guest";
        } else {
          this.userName = "Guest"; // Fallback to "Guest"
        }
      } catch (error) {
        console.error("Error fetching user data:", error);

        // Handle specific errors, such as "Unauthenticated"
        if (error.response && error.response.status === 401) {
          console.warn("Unauthenticated request. Redirecting to login page.");
          this.$router.push("/admin/login"); // Redirect to login if unauthenticated
        } else {
          this.userName = "Guest"; // Default if another error occurs
        }
      }
    },

    urlProposal(t) {
      if (!t?.document) return '#';
      return mediaUrl(t.document) || t.document;
    },

    saatKlikUnduhProposal(t, event) {
      if (this.mengunduhProposal[t.id] === 'loading') {
        if (event) event.preventDefault();
        return;
      }

      this.mengunduhProposal = { ...this.mengunduhProposal, [t.id]: 'loading' };
      if (this.toast) {
        this.toast.info('Memulai unduhan berkas proposal... Mohon tunggu sebentar.');
      }

      setTimeout(() => {
        this.mengunduhProposal = { ...this.mengunduhProposal, [t.id]: 'success' };
        setTimeout(() => {
          const salinan = { ...this.mengunduhProposal };
          delete salinan[t.id];
          this.mengunduhProposal = salinan;
        }, 2500);
      }, 3500);
    },

    downloadDocument(documentName) {
      if (!documentName) return;
      const url = mediaUrl(documentName) || documentName;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    // Sample method to load tournament data
    async loadTournamentData(page = 1) {
      this.loading = true;
      try {
        const response = await axios.get("/tournaments", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`, // Assumes token is in localStorage
          },
          params: {
            page,
            perPage: this.perPage,
            search: this.searchQuery.trim(),
          },
        });
        const {
          current_page,
          last_page,
          data,
          next_page_url,
          prev_page_url,
        } = response.data;

        this.tournaments = data; // Assign team members
        this.currentPage = current_page; // Current page
        this.totalPages = last_page; // Total pages
        this.nextPageUrl = next_page_url; // Next page URL
        this.prevPageUrl = prev_page_url; // Previous page URL
        this.loading = false;
      } catch (error) {
        console.error("Error loading members:", error);
      }
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.loadTournamentData(page);
      }
    },
  },
};
</script>

<style scoped>
.dashboard { background-color: transparent; margin-top: 0; }

@media (max-width: 768px) {
  .dashboard { margin-top: 0; }
}

/* ── Hero Welcome Card ── */
.dashboard-hero {
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-2xl);
  padding: 1.75rem 2rem;
  box-shadow: var(--shadow-card);
  position: relative;
  overflow: hidden;
}

.dashboard-hero::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(240, 80, 8, 0.08) 0%, transparent 70%);
  pointer-events: none;
}

.dashboard-hero__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dashboard-hero__left {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.dashboard-hero__avatar {
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  font-size: 2.2rem;
  color: var(--accent);
  background: linear-gradient(135deg, rgba(240, 80, 8, 0.12) 0%, rgba(248, 168, 8, 0.18) 100%);
  border-radius: 50%;
  border: 2px solid rgba(240, 80, 8, 0.25);
  flex-shrink: 0;
}

.dashboard-hero__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 3px 10px;
  border-radius: var(--radius-full);
  margin-bottom: 0.35rem;
  width: fit-content;
}

.badge-panitia {
  background: rgba(240, 80, 8, 0.12);
  color: var(--accent);
  border: 1px solid rgba(240, 80, 8, 0.25);
}

.badge-official {
  background: rgba(22, 163, 74, 0.12);
  color: var(--success-600);
  border: 1px solid rgba(22, 163, 74, 0.25);
}

.dashboard-hero__title {
  margin: 0 0 0.35rem;
  font-family: var(--font-sans);
  font-size: 1.45rem;
  font-weight: 800;
  color: var(--text-primary);
  letter-spacing: -0.02em;
}

.dashboard-hero__title span {
  color: var(--accent);
}

.dashboard-hero__subtitle {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: 1.5;
  max-width: 700px;
}

@media (max-width: 768px) {
  .dashboard-hero { padding: 1.25rem; border-radius: var(--radius-xl); }
  .dashboard-hero__left { flex-direction: column; align-items: flex-start; gap: 0.75rem; }
  .dashboard-hero__avatar { width: 44px; height: 44px; font-size: 1.6rem; }
  .dashboard-hero__title { font-size: 1.15rem; }
}

/* ── Section Title ── */
.section-title {
  font-family: var(--font-sans);
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--text-primary);
  letter-spacing: -0.01em;
  margin: 0;
}

.text-accent { color: var(--accent); }

/* ── Grid Kartu ── */
.kartu-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: var(--space-4);
}

@media (min-width: 768px) {
  .kartu-grid { grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: var(--space-5); }
}

.kartu {
  display: flex;
  flex-direction: column;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-base);
}

.kartu:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-card);
  border-color: rgba(240, 80, 8, 0.3);
}

.kartu__poster { position: relative; }

.kartu__status {
  position: absolute;
  bottom: var(--space-2);
  right: var(--space-2);
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: 0.65rem;
  font-weight: 800;
  color: #fff;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(8px);
  border-radius: var(--radius-full);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.kartu__status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.kartu__status--success { background: rgba(21, 128, 61, 0.85); }
.kartu__status--warning { background: rgba(232, 72, 8, 0.85); }

.kartu__isi {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  flex: 1;
}

.kartu__judul {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: 700;
  line-height: 1.35;
  color: var(--text-primary);
  overflow-wrap: anywhere;
}

.kartu__aksi {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-top: auto;
}

.kartu__aksi .button {
  width: 100%;
  justify-content: center;
  padding-inline: var(--space-3);
  font-size: var(--text-xs);
  min-height: 38px;
}

.kartu-kosong {
  grid-column: 1 / -1;
  padding: 3rem 1.5rem;
  text-align: center;
  color: var(--text-muted);
  background: var(--bg-surface);
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-xl);
}

.empty-icon-circle {
  display: inline-grid;
  place-items: center;
  width: 60px;
  height: 60px;
  background: var(--bg-sunken);
  border-radius: 50%;
  margin: 0 auto;
}

/* ── Animasi & Status Unduh Proposal ── */
.button.is-downloading {
  cursor: wait !important;
  pointer-events: none;
  opacity: 0.85;
  border-color: rgba(239, 68, 68, 0.4);
}

.button.is-success {
  border-color: rgba(16, 185, 129, 0.5) !important;
  color: #10b981 !important;
}

@keyframes putar-halus {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.ikon-putar {
  animation: putar-halus 0.85s linear infinite;
  display: inline-block;
}

@keyframes pop-masuk {
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.25); }
  100% { transform: scale(1); opacity: 1; }
}

.ikon-sukses {
  animation: pop-masuk 0.28s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  display: inline-block;
}
</style>
